import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";

import {
  useRestaurantSession,
} from "./RestaurantSessionProvider";


const CartContext = createContext();


export function CartProvider({
  children,
  restaurantId,
}) {

  /*
   * Get the unique dining session
   * created by RestaurantSessionProvider.
   *
   * Example:
   *
   * Restaurant 8
   * Table 2
   * Session:
   * 550e8400-e29b-41d4-a716-446655440000
   */

  const {
    sessionToken,
  } = useRestaurantSession();


  /*
   * IMPORTANT
   *
   * The cart is now identified by
   * the dining session.
   *
   * OLD:
   *
   * restaurant_cart_8
   *
   * NEW:
   *
   * restaurant_cart_<sessionToken>
   */

  const CART_STORAGE_KEY =
    sessionToken
      ? `restaurant_cart_${sessionToken}`
      : null;


  /*
   * Cart state.
   *
   * Start empty.
   *
   * The correct cart will be loaded
   * when the sessionToken becomes available.
   */

  const [cart, setCart] = useState([]);


  /*
   * LOAD CART
   *
   * This runs whenever the dining
   * session changes.
   */

  useEffect(() => {

    if (!CART_STORAGE_KEY) {

      setCart([]);

      return;

    }


    try {

      const savedCart =
        sessionStorage.getItem(
          CART_STORAGE_KEY
        );


      if (savedCart) {

        setCart(
          JSON.parse(savedCart)
        );

      } else {

        setCart([]);

      }

    } catch (error) {

      console.error(
        "Failed to load cart:",
        error
      );

      setCart([]);

    }

  }, [
    CART_STORAGE_KEY,
  ]);


  /*
   * SAVE CART
   */

  const saveCart = (newCart) => {

    if (!CART_STORAGE_KEY) {

      return;

    }


    try {

      sessionStorage.setItem(
        CART_STORAGE_KEY,
        JSON.stringify(newCart)
      );

    } catch (error) {

      console.error(
        "Failed to save cart:",
        error
      );

    }

  };


  /*
   * ADD TO CART
   */

  const addToCart = (item) => {

    /*
     * Do not allow cart operations
     * until a dining session exists.
     */

    if (!sessionToken) {

      console.warn(
        "Cannot add item before dining session is ready."
      );

      return;

    }


    setCart((currentCart) => {

      const existingItem =
        currentCart.find(
          (cartItem) =>
            cartItem.id === item.id
        );


      let newCart;


      if (existingItem) {

        newCart =
          currentCart.map(
            (cartItem) =>
              cartItem.id === item.id
                ? {
                    ...cartItem,

                    quantity:
                      cartItem.quantity + 1,
                  }
                : cartItem
          );

      } else {

        newCart = [
          ...currentCart,

          {
            ...item,

            quantity: 1,
          },
        ];

      }


      saveCart(newCart);


      return newCart;

    });

  };


  /*
   * INCREASE QUANTITY
   */

  const increaseQuantity = (id) => {

    if (!sessionToken) {

      return;

    }


    setCart((currentCart) => {

      const newCart =
        currentCart.map(
          (item) =>
            item.id === id
              ? {
                  ...item,

                  quantity:
                    item.quantity + 1,
                }
              : item
        );


      saveCart(newCart);


      return newCart;

    });

  };


  /*
   * DECREASE QUANTITY
   */

  const decreaseQuantity = (id) => {

    if (!sessionToken) {

      return;

    }


    setCart((currentCart) => {

      const newCart =
        currentCart

          .map(
            (item) =>
              item.id === id
                ? {
                    ...item,

                    quantity:
                      item.quantity - 1,
                  }
                : item
          )

          .filter(
            (item) =>
              item.quantity > 0
          );


      saveCart(newCart);


      return newCart;

    });

  };


  /*
   * REMOVE ITEM
   */

  const removeFromCart = (id) => {

    if (!sessionToken) {

      return;

    }


    setCart((currentCart) => {

      const newCart =
        currentCart.filter(
          (item) =>
            item.id !== id
        );


      saveCart(newCart);


      return newCart;

    });

  };


  /*
   * CLEAR CART
   *
   * Used after successful order/payment.
   */

  const clearCart = () => {

    setCart([]);


    if (!CART_STORAGE_KEY) {

      return;

    }


    try {

      sessionStorage.removeItem(
        CART_STORAGE_KEY
      );

    } catch (error) {

      console.error(
        "Failed to clear cart:",
        error
      );

    }

  };


  /*
   * TOTAL NUMBER OF ITEMS
   */

  const cartCount = useMemo(() => {

    return cart.reduce(
      (total, item) =>
        total + item.quantity,

      0
    );

  }, [cart]);


  /*
   * TOTAL CART PRICE
   */

  const cartTotal = useMemo(() => {

    return cart.reduce(
      (total, item) =>
        total +
        Number(item.price) *
        item.quantity,

      0
    );

  }, [cart]);


  return (

    <CartContext.Provider
      value={{

        cart,

        addToCart,

        increaseQuantity,

        decreaseQuantity,

        removeFromCart,

        clearCart,

        cartCount,

        cartTotal,

        restaurantId,

        sessionToken,

      }}
    >

      {children}

    </CartContext.Provider>

  );

}


export function useCart() {

  return useContext(
    CartContext
  );

}